import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { api } from '../../lib/api';
import { soldesFournisseurs } from '../../lib/argentApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate, formatMontant } from '../../lib/formats';
import { nomLisible, referenceLisible } from '../../lib/libelles';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import { usePagination } from '../../hooks/usePagination';
import FinancialStatement, { financialKey } from '../FinancialStatement';
import { BarrePagination } from '../ui/pagination-bar';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { LoadingState } from '../ui/loading-state';
import { SearchableSelect } from '../ui/searchable-select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { toast } from '../ui/sonner';
import DetailEcriture from './DetailEcriture';
import { FenetreGeste } from './NouvelleOperation';
import TableauSoldes, { ilYA } from './TableauSoldes';

/**
 * Onglet « Fournisseurs » de l'écran Argent (revue de l'écran Argent, lot 7).
 *
 * Combien on doit, à qui: une ligne par fournisseur — les transporteurs des
 * lots et des états de transport et les propriétaires de camions loués
 * compris, puisque leurs décomptes sont des factures fournisseur du journal.
 * « Payer » ouvre la même fenêtre que « Nouvelle opération ». Renvois vers
 * Lots › Paiements et Locations pour le détail de ces décomptes.
 */

const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? 's' : ''}`;
const ORIGINES = { LOT: 'Lots', TRANSPORT: 'Transport', LOCATION: 'Location' };

function NouveauFournisseur({ ouvert, onFermer, onTermine }) {
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [cle, setCle] = useState(financialKey);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => {
    if (!ouvert) return;
    setNom('');
    setTelephone('');
    setCle(financialKey());
  }, [ouvert]);
  const enregistrer = async () => {
    setEnvoi(true);
    try {
      await api.post('/finance/suppliers', { name: nom.trim(), phone: telephone.trim() || null, idempotency_key: cle });
      toast.success('Fournisseur ajouté.');
      onTermine?.();
    } catch (e) {
      toast.error(messageErreur(e, 'Le fournisseur n’a pas pu être ajouté.'));
    } finally {
      setEnvoi(false);
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onFermer()}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Ajouter un fournisseur</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm font-medium sm:col-span-2">
            Nom
            <Input aria-label="Nom du fournisseur" value={nom} onChange={(e) => { setNom(e.target.value); setCle(financialKey()); }} className="mt-1" autoFocus />
          </label>
          <label className="block text-sm font-medium">
            Téléphone
            <Input aria-label="Téléphone" value={telephone} onChange={(e) => { setTelephone(e.target.value); setCle(financialKey()); }} className="mt-1" />
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onFermer}>Annuler</Button>
          <Button type="button" disabled={envoi || !enLigne || !nom.trim()} onClick={enregistrer}>{envoi ? 'Ajout…' : 'Ajouter'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Achats des stations enregistrés avant le journal: chacun se reprend chez son fournisseur. */
function AchatsARapprocher({ ouvert, onFermer, onModifie }) {
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [achats, setAchats] = useState(null);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [choix, setChoix] = useState({});
  const [envoi, setEnvoi] = useState('');
  const charger = useCallback(async () => {
    try {
      const [liste, tiers] = await Promise.all([api.get('/finance/purchases-to-review'), api.get('/finance/suppliers')]);
      setAchats((liste?.data?.data || []).filter((a) => !a.adopted));
      setFournisseurs(tiers?.data?.data || []);
    } catch (e) {
      setAchats([]);
      toast.error(messageErreur(e, 'Les achats à rapprocher sont indisponibles.'));
    }
  }, []);
  useEffect(() => {
    if (ouvert) charger();
  }, [ouvert, charger]);
  const options = useMemo(() => fournisseurs.map((f) => ({ value: f.id, label: nomLisible(f.name, 'Fournisseur') })), [fournisseurs]);
  const pagination = usePagination(achats || [], { cle: 'achats' });
  const reprendre = async (achatId) => {
    setEnvoi(achatId);
    try {
      await api.post(`/finance/purchases/${achatId}/adopt-history`, { supplier_id: choix[achatId], idempotency_key: financialKey() });
      toast.success('Facture reprise chez le fournisseur.');
      await charger();
      onModifie?.();
    } catch (e) {
      toast.error(messageErreur(e, 'La reprise a échoué.'));
    } finally {
      setEnvoi('');
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onFermer()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Achats des stations à reprendre</DialogTitle></DialogHeader>
        {!achats && <LoadingState shape="table" rows={3} columns={4} label="Chargement" />}
        {achats && achats.length === 0 && <p className="text-sm text-muted-foreground">Aucun achat à reprendre.</p>}
        {achats && achats.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-1.5 font-semibold">Achat</th>
                <th className="py-1.5 text-right font-semibold">Montant</th>
                <th className="py-1.5 font-semibold">Fournisseur</th>
                <th className="py-1.5"><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            <tbody>
              {pagination.lignes.map((a) => (
                <tr key={a.id} className="border-b last:border-0">
                  <td className="py-1.5">{[referenceLisible(a.reference_number, 'Achat'), a.purchase_date ? formatDate(a.purchase_date) : null, a.supplier_name].filter(Boolean).join(' · ')}</td>
                  <td className="py-1.5 text-right tabular-nums">{formatMontant(a.total_cost)}</td>
                  <td className="w-56 py-1.5 pl-3">
                    <SearchableSelect aria-label="Fournisseur de l’achat" value={choix[a.id] || ''} onValueChange={(v) => setChoix((c) => ({ ...c, [a.id]: v }))} options={options} placeholder="Choisir" searchPlaceholder="Rechercher…" emptyMessage="Aucun fournisseur" />
                  </td>
                  <td className="py-1.5 pl-2 text-right">
                    <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={!enLigne || !choix[a.id] || envoi === a.id} onClick={() => reprendre(a.id)}>Reprendre</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {achats && <BarrePagination pagination={pagination} nom="achats" />}
      </DialogContent>
    </Dialog>
  );
}

function PanneauFournisseur({ fournisseurId, ligne, peutSaisir, onFermer, onGeste, rafraichir, onModifie }) {
  const [ecriture, setEcriture] = useState(null);
  return (
    <Sheet open={Boolean(fournisseurId)} onOpenChange={(ouvert) => !ouvert && onFermer()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-4xl" data-testid="panneau-fournisseur">
        <SheetHeader>
          <SheetTitle>{nomLisible(ligne?.name, 'Fournisseur')}</SheetTitle>
          <SheetDescription>
            {ligne
              ? [`reste à payer ${formatMontant(ligne.balance)}`, `factures reçues ${formatMontant(ligne.bills)}`, `payé ${formatMontant(ligne.paid)}`].join(' · ')
              : 'Ce qu’on doit à ce fournisseur'}
          </SheetDescription>
        </SheetHeader>
        {peutSaisir && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={() => onGeste('fournisseur')}>Payer</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => onGeste('facture-recue')}>Facture reçue</Button>
          </div>
        )}
        <div className="mt-5">
          <FinancialStatement partyType="SUPPLIER" partyId={fournisseurId} refresh={rafraichir} onSelect={(operation) => setEcriture(operation.id)} />
        </div>
        <DetailEcriture operationId={ecriture} onFermer={() => setEcriture(null)} onModifie={onModifie} peutSaisir={peutSaisir} />
      </SheetContent>
    </Sheet>
  );
}

export default function FournisseursArgent({ fournisseurChoisi, onChoisirFournisseur, peutSaisir = true, nombreAPayer = 0, achatsAReprendre = 0, avecLots = false, avecLocations = false, onModifie }) {
  const [geste, setGeste] = useState(null);
  const [fenetre, setFenetre] = useState(null);
  const [rafraichir, setRafraichir] = useState(0);
  const [lignesConnues, setLignesConnues] = useState({});
  const apresGeste = () => {
    setGeste(null);
    setFenetre(null);
    setRafraichir((n) => n + 1);
    onModifie?.();
  };
  const charger = useCallback(async (params) => {
    const donnees = await soldesFournisseurs(params);
    setLignesConnues((precedent) => ({ ...precedent, ...Object.fromEntries((donnees?.items || []).map((l) => [l.supplier_id, l])) }));
    return donnees;
  }, []);
  const colonnes = [
    { cle: 'origins', titre: 'Origine', classe: 'text-muted-foreground', rendu: (l) => (l.origins || []).map((o) => ORIGINES[o] || o).join(' · '), masquee: (t) => !Number(t.with_origin) },
    { cle: 'bills', titre: 'Factures reçues', montant: true, tri: 'facture' },
    { cle: 'credits', titre: 'Avoirs', montant: true, masquee: (t) => !Number(t.credits) },
    { cle: 'paid', titre: 'Payé', montant: true },
    { cle: 'refunded', titre: 'Remboursé', montant: true, masquee: (t) => !Number(t.refunded) },
    { cle: 'balance', titre: 'Reste à payer', montant: true, tri: 'reste', classe: 'font-medium' },
    {
      cle: 'oldest_open_age_days',
      titre: 'Plus ancienne',
      tri: 'anciennete',
      rendu: (l) => (l.oldest_open_date ? <span title={`Facture du ${formatDate(l.oldest_open_date)}`}>{ilYA(l.oldest_open_age_days)}</span> : ''),
      total: (t) => (Number(t.open_bills) ? pluriel(Number(t.open_bills), 'facture') : ''),
    },
  ];
  const renvois = [
    avecLots && { to: '/lots/paiements', libelle: 'Partenaires des lots' },
    avecLocations && { to: '/rentals?onglet=entrantes', libelle: 'Camions loués' },
  ].filter(Boolean);
  return (
    <>
      {nombreAPayer === null ? <LoadingState shape="table" rows={6} columns={6} label="Chargement des fournisseurs" /> : (
        <TableauSoldes
          cle="fournisseurs"
          testid="tableau-fournisseurs"
          charger={charger}
          colonnes={colonnes}
          identifiant={(l) => l.supplier_id}
          nom={{ titre: 'Fournisseur', tri: 'nom', rendu: (l) => nomLisible(l.name, 'Fournisseur') }}
          detailMobile={(l) => [(l.origins || []).map((o) => ORIGINES[o] || o).join(' · '), l.oldest_open_date ? ilYA(l.oldest_open_age_days) : null].filter(Boolean).join(' · ')}
          montantMobile={{ cle: 'balance', titre: 'Reste' }}
          filtre={{ libelle: 'Reste à payer', param: 'avec_reste', actifParDefaut: nombreAPayer > 0 }}
          triParDefaut="reste"
          rechercheLibelle="Rechercher un fournisseur…"
          totalLibelle={(n) => `Total (${pluriel(n, 'fournisseur')})`}
          domaines={['finance', 'paiements', 'depenses', 'lots', 'locations']}
          rafraichir={rafraichir}
          vide="Aucun fournisseur à payer."
          action={peutSaisir ? (l) => (Number(l.balance) > 0 ? (
            <Button type="button" size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={() => setGeste({ geste: 'fournisseur', fournisseur: l.supplier_id })}>Payer</Button>
          ) : null) : undefined}
          onOuvrir={(l) => onChoisirFournisseur(l.supplier_id)}
          barre={(
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              {achatsAReprendre > 0 && (
                <button type="button" className="inline-flex h-8 items-center rounded-full border border-flame/30 bg-flame/10 px-3 text-xs font-medium text-flame-ink" onClick={() => setFenetre('achats')}>
                  {`Achats à reprendre (${achatsAReprendre})`}
                </button>
              )}
              {renvois.map((r) => (
                <Link key={r.to} to={r.to} className="inline-flex h-8 items-center rounded-full border bg-card px-3 text-xs font-medium hover:bg-muted">{`${r.libelle} →`}</Link>
              ))}
              {peutSaisir && (
                <Button type="button" size="sm" variant="ghost" className="h-8 gap-1.5" onClick={() => setFenetre('nouveau')}>
                  <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter
                </Button>
              )}
            </div>
          )}
        />
      )}
      <PanneauFournisseur
        fournisseurId={fournisseurChoisi || null}
        ligne={lignesConnues[fournisseurChoisi]}
        peutSaisir={peutSaisir}
        onFermer={() => onChoisirFournisseur('')}
        onGeste={(cle) => setGeste({ geste: cle, fournisseur: fournisseurChoisi })}
        rafraichir={rafraichir}
        onModifie={apresGeste}
      />
      <FenetreGeste geste={geste?.geste} ouvert={Boolean(geste)} prefill={{ fournisseur: geste?.fournisseur }} onFermer={() => setGeste(null)} onTermine={apresGeste} />
      <NouveauFournisseur ouvert={fenetre === 'nouveau'} onFermer={() => setFenetre(null)} onTermine={apresGeste} />
      <AchatsARapprocher ouvert={fenetre === 'achats'} onFermer={() => setFenetre(null)} onModifie={apresGeste} />
    </>
  );
}
